// Partners page — list + invite + delete.
//
// Invite flow: admin fills email + companyName + (optional) phone/city.
// Backend writes a row in email_verifications and emails a magic link
// to the partner via Resend SMTP. The partner clicks the link, lands on
// /verify-email, sets their own password, and the cabinet+user are
// provisioned at that moment (Shanyrak company UUID + genesis JWT +
// partners + users). Admin never sees or sets the password — the partner
// owns it from the start.

import { useEffect, useState, type FormEvent } from "react";
import { gql } from "../lib/gql";
import { PartnerApiTokensModal } from "./PartnerApiTokens";

type AdminPartner = {
  id: string;
  code: string;
  companyName: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  externalCompanyId: string | null;
  pointsCount: number;
  devicesCount: number;
  usersCount: number;
  createdAt: string;
};

type InviteResult = {
  email: string;
  expiresAt: string;
};

const LIST_QUERY = `
  query AdminPartners($limit: Int, $offset: Int) {
    adminPartners(limit: $limit, offset: $offset) {
      id
      code
      companyName
      email
      phone
      city
      externalCompanyId
      pointsCount
      devicesCount
      usersCount
      createdAt
    }
  }
`;

const INVITE_MUTATION = `
  mutation AdminCreatePartner($input: AdminCreatePartnerInput!) {
    adminCreatePartner(input: $input) {
      email
      expiresAt
    }
  }
`;

// Мета-админ — служебная компания платформы; API-токены ей не выдаются
// (сервер тоже откажет).
const META_ADMIN_CODE = "META00";

const DELETE_MUTATION = `
  mutation AdminDeletePartner($partnerId: UUID!) {
    adminDeletePartner(partnerId: $partnerId)
  }
`;

// Вход в кабинет компании: сервер выдаёт временный токен (4 часа или до
// «Выйти» в кабинете), кабинет принимает его из адреса после «#» — эта часть
// адреса на сервер не уходит. Каждый вход — в журнале действий.
const IMPERSONATE_MUTATION = `
  mutation AdminImpersonatePartner($partnerId: UUID!) {
    adminImpersonatePartner(partnerId: $partnerId) { token cabinetUrl }
  }
`;

export function PartnersPage() {
  const [partners, setPartners] = useState<AdminPartner[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [invited, setInvited] = useState<InviteResult | null>(null);
  const [tokensFor, setTokensFor] = useState<AdminPartner | null>(null);

  async function reload() {
    try {
      const data = await gql<{ adminPartners: AdminPartner[] }>(LIST_QUERY, {
        limit: 200,
        offset: 0,
      });
      setPartners(data.adminPartners);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleImpersonate(p: AdminPartner) {
    // Вкладку открываем сразу, по клику: открытую после ответа сервера
    // браузер счёл бы всплывающим окном и заблокировал.
    const tab = window.open("about:blank", "_blank");
    try {
      const data = await gql<{ adminImpersonatePartner: { token: string; cabinetUrl: string } }>(
        IMPERSONATE_MUTATION,
        { partnerId: p.id },
      );
      const { token, cabinetUrl } = data.adminImpersonatePartner;
      const url = `${cabinetUrl}#${token}`;
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      tab?.close();
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleDelete(p: AdminPartner) {
    const ok = window.confirm(
      `Удалить партнёра "${p.companyName}" (${p.code})?\n\n` +
        `Это необратимо. Все ${p.pointsCount} точек, ${p.devicesCount} устройств и ` +
        `${p.usersCount} пользователей будут удалены каскадно.`
    );
    if (!ok) return;
    try {
      await gql(DELETE_MUTATION, { partnerId: p.id });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Партнёры ({partners.length})</h2>
        <button onClick={() => setShowCreate(true)}>+ Пригласить партнёра</button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {showCreate && (
        <InvitePartnerForm
          onCancel={() => setShowCreate(false)}
          onInvited={(result) => {
            setShowCreate(false);
            setInvited(result);
            // List won't change yet — partner only appears after they
            // click the magic link and set a password. But refresh anyway
            // in case multiple admins are working in parallel.
            reload();
          }}
        />
      )}

      {invited && (
        <InvitedModal email={invited.email} expiresAt={invited.expiresAt} onClose={() => setInvited(null)} />
      )}

      {tokensFor && (
        <PartnerApiTokensModal
          partnerId={tokensFor.id}
          companyName={tokensFor.companyName}
          onClose={() => setTokensFor(null)}
        />
      )}

      <div className="card">
        {partners.length === 0 ? (
          <div className="muted">Партнёров пока нет. Пригласите первого кнопкой сверху.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Код</th>
                <th>Компания</th>
                <th>Контакт</th>
                <th>Точки</th>
                <th>Устройства</th>
                <th>Юзеры</th>
                <th>Создан</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontFamily: "monospace" }}>{p.code}</td>
                  <td>{p.companyName}</td>
                  <td className="muted">
                    {[p.email, p.phone, p.city].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td>{p.pointsCount}</td>
                  <td>{p.devicesCount}</td>
                  <td>{p.usersCount}</td>
                  <td className="muted">
                    {new Date(p.createdAt).toLocaleDateString("ru-RU")}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, flexWrap: "nowrap" }}>
                      {p.code !== META_ADMIN_CODE && (
                        <button
                          style={{ fontSize: 12, padding: "4px 8px", whiteSpace: "nowrap" }}
                          onClick={() => handleImpersonate(p)}
                          title="Открыть кабинет компании с правами её владельца на 4 часа"
                        >
                          Войти в кабинет
                        </button>
                      )}
                      {p.code !== META_ADMIN_CODE && (
                        <button
                          className="secondary"
                          style={{ fontSize: 12, padding: "4px 8px", whiteSpace: "nowrap" }}
                          onClick={() => setTokensFor(p)}
                        >
                          API-токены
                        </button>
                      )}
                      <button
                        className="secondary"
                        style={{ fontSize: 12, padding: "4px 8px" }}
                        onClick={() => handleDelete(p)}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InvitePartnerForm({
  onCancel,
  onInvited,
}: {
  onCancel: () => void;
  onInvited: (result: InviteResult) => void;
}) {
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  // Язык письма и будущего кабинета: адресата ещё нет, спросить не у кого —
  // выбирает тот, кто приглашает.
  const [language, setLanguage] = useState("ru");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await gql<{ adminCreatePartner: InviteResult }>(INVITE_MUTATION, {
        input: {
          email: email.trim(),
          companyName: companyName.trim(),
          phone: phone.trim() || null,
          city: city.trim() || null,
          language,
        },
      });
      onInvited(data.adminCreatePartner);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="card" onSubmit={onSubmit} style={{ background: "var(--bg-elevated)" }}>
      <h3 style={{ margin: "0 0 12px 0" }}>Пригласить партнёра</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Партнёр получит письмо со ссылкой для активации на выбранном языке — он же
        станет языком его писем. На странице активации он сам
        задаст пароль и сразу войдёт в кабинет. Партнёр появится в этом списке только
        после того, как он подтвердит email — до этого момента ни запись в БД, ни
        Shanyrak tenant не создаётся.
      </p>

      {error && <div className="error-banner">{error}</div>}

      <div className="form-row">
        <label>Email партнёра *</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="partner@example.kz"
        />
      </div>
      <div className="form-row">
        <label>Название компании *</label>
        <input
          type="text"
          required
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="ТОО Пример"
        />
      </div>
      <div className="row" style={{ gap: 12 }}>
        <div className="form-row" style={{ flex: 1 }}>
          <label>Телефон</label>
          <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="form-row" style={{ flex: 1 }}>
          <label>Город</label>
          <input type="text" value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <div className="form-row" style={{ flex: 1 }}>
          <label>Язык письма</label>
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="ru">Русский</option>
            <option value="en">English</option>
            <option value="tr">Türkçe</option>
          </select>
        </div>
      </div>

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button type="button" className="secondary" onClick={onCancel} disabled={loading}>
          Отмена
        </button>
        <button type="submit" disabled={loading}>
          {loading ? "Отправляем…" : "Отправить приглашение"}
        </button>
      </div>
    </form>
  );
}

// Modal shown after invite — confirms the magic link was sent and tells
// the admin when it expires so they can re-invite if needed.
function InvitedModal({
  email,
  expiresAt,
  onClose,
}: {
  email: string;
  expiresAt: string;
  onClose: () => void;
}) {
  const expDate = new Date(expiresAt).toLocaleString("ru-RU");
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ maxWidth: 480, width: "90%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 12px 0", color: "var(--success)" }}>
          ✓ Приглашение отправлено
        </h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Письмо со ссылкой ушло на <strong>{email}</strong>. Партнёр перейдёт по ссылке,
          задаст пароль и войдёт в кабинет.
        </p>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Ссылка действительна до <strong>{expDate}</strong>. Если партнёр пропустит срок —
          пригласите его снова кнопкой «Пригласить партнёра».
        </p>

        <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose}>Готово</button>
        </div>
      </div>
    </div>
  );
}
